import styled from 'styled-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogout } from '../hooks/useLogout';
import { LayoutDashboard, Scan, Heart, ShieldCheck, Settings, LogOut } from 'lucide-react';
import { StorageKeys } from '../utils/storage';
import { useMemo } from 'react';

// Backdrop overlay
const Backdrop = styled.div<{ isClosing: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  z-index: 9998;
  animation: ${({ isClosing }) => isClosing ? 'fadeOut' : 'fadeIn'} 0.2s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes fadeOut {
    from { opacity: 1; }
    to { opacity: 0; }
  }
`;

// Dropdown menu container
const DropdownMenu = styled.div<{ isClosing: boolean }>`
  position: fixed;
  top: 80px;
  right: 1.5rem;
  width: 280px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  overflow: hidden;
  animation: ${({ isClosing }) => isClosing ? 'slideUp' : 'slideDown'} 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes slideUp {
    from {
      opacity: 1;
      transform: translateY(0);
    }
    to {
      opacity: 0;
      transform: translateY(-10px);
    }
  }
`;

// Menu section
const MenuList = styled.div`
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MenuItem = styled.button<{ variant?: 'default' | 'danger' }>`
  width: 100%;
  padding: 0.875rem 1rem;
  background: ${({ variant }) => variant === 'danger' ? 'rgba(239, 68, 68, 0.1)' : 'transparent'};
  border: none;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${({ variant }) => variant === 'danger' ? '#dc2626' : '#0f172a'};
  font-size: 0.9375rem;
  font-weight: 600;
  text-align: left;

  &:hover {
    background: ${({ variant }) => variant === 'danger' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(102, 126, 234, 0.1)'};
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    flex-shrink: 0;
    opacity: 0.8;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 0.5rem 0;
`;

const SettingsBars = ({ open, onClose, cameras, isLicenseValid }) => {
  const navigate = useNavigate();
  const logout = useLogout();
  const [isClosing, setIsClosing] = useState(false);

  const pid = useMemo(() => localStorage.getItem(StorageKeys.PATIENT_ID) || '', []);
  const isPidValid = pid && pid !== 'null';
  const orgId = useMemo(() => localStorage.getItem(StorageKeys.ORG_ID) || '', []);
  const isOrgIdValid = orgId && orgId !== 'null';

  const user = useMemo(() => {
    const userStr = localStorage.getItem(StorageKeys.AUTH_USER);
    return userStr ? JSON.parse(userStr) : null;
  }, []);
  const isOrgType3 = user?.org_type === 3;

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose({ cameraId: cameras?.[0]?.deviceId });
      setIsClosing(false);
    }, 250);
  }, [cameras, onClose]);

  const handleNavigate = useCallback((path: string) => {
    handleClose();
    setTimeout(() => navigate(path), 100);
  }, [navigate, handleClose]);

  const handleLogout = useCallback(() => {
    handleClose();
    setTimeout(() => logout(), 100);
  }, [logout, handleClose]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [open, handleClose]);

  if (!open) return null;

  return (
    <>
      <Backdrop isClosing={isClosing} onClick={handleClose} />
      <DropdownMenu isClosing={isClosing}>
        <MenuList>
          <MenuItem onClick={() => handleNavigate('/dashboard')}>
            <LayoutDashboard size={18} />
            <span>Dashboard</span>
          </MenuItem>

          {isOrgIdValid && isOrgType3 && (
            <MenuItem onClick={() => handleNavigate('/fullScan')}>
              <Scan size={18} />
              <span>Full Scan</span>
            </MenuItem>
          )}

          {isPidValid && (
            <MenuItem onClick={() => handleNavigate('/scan')}>
              <Scan size={18} />
              <span>Start Scan</span>
            </MenuItem>
          )}

          {(isPidValid || (isOrgIdValid && isOrgType3)) && (
            <>
              <MenuItem onClick={() => handleNavigate('/vitals')}>
                <Heart size={18} />
                <span>{isOrgIdValid && isOrgType3 ? 'Vitals' : 'My Vitals'}</span>
              </MenuItem>
              
              <MenuItem onClick={() => handleNavigate('/licenseInfo')}>
                <ShieldCheck size={18} />
                <span>License Info</span>
              </MenuItem>
            </>
          )}

          <MenuItem onClick={() => handleNavigate('/settings')}>
            <Settings size={18} />
            <span>Settings</span>
          </MenuItem>

          <MenuDivider />

          <MenuItem variant="danger" onClick={handleLogout}>
            <LogOut size={18} />
            <span>Sign Out</span>
          </MenuItem>
        </MenuList>
      </DropdownMenu>
    </>
  );
};

export default SettingsBars;
